import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import StaffHome from './pages/StaffHome';
import StaffDashboard from './pages/StaffDashboard';
import AdminDashboard from './pages/AdminDashboard';
import RecipeDetail from './pages/RecipeDetail';
import BatchHistory from './pages/BatchHistory';
import BatchRecipes from './pages/BatchRecipes';
import AssemblySOPs from './pages/AssemblySOPs';
import ChecklistsHome from './pages/ChecklistsHome';
import ChecklistPage from './pages/ChecklistPage';
import CustomerSOP from './pages/CustomerSOP';
import FAQsPage from './pages/FAQsPage';
import { Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />}
          />
          <Route
            path="/"
            element={
              user ? (
                user.role === 'admin' ? (
                  <AdminDashboard user={user} onLogout={handleLogout} />
                ) : (
                  <Navigate to="/staff-home" />
                )
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          {/* Staff Routes */}
          <Route
            path="/staff-home"
            element={user && user.role === 'staff' ? <StaffHome user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/batch-recipes"
            element={user && user.role === 'staff' ? <BatchRecipes user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/assembly-sops"
            element={user && user.role === 'staff' ? <AssemblySOPs user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/checklists"
            element={user && user.role === 'staff' ? <ChecklistsHome user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/checklist/:id"
            element={user && user.role === 'staff' ? <ChecklistPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/customer-sop"
            element={user && user.role === 'staff' ? <CustomerSOP user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/faqs"
            element={user && user.role === 'staff' ? <FAQsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          {/* Shared Routes */}
          <Route
            path="/recipe/:id"
            element={user ? <RecipeDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
          <Route
            path="/batches"
            element={user ? <BatchHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;